import React, { useState, useEffect } from 'react';
import { Github, Linkedin, Mail, Phone, MapPin, Code, Briefcase, Menu, X, ExternalLink, Quote } from 'lucide-react';

const FlutterPortfolio = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
    
    // Add custom CSS animations
    const style = document.createElement('style');
    style.textContent = `
      @keyframes float {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(-10px); }
      }
      
      @keyframes shimmer {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(100%); }
      }
      
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      
      .animate-float {
        animation: float 3s ease-in-out infinite;
      }
      
      .animate-shimmer {
        animation: shimmer 2s ease-in-out infinite;
      }
      
      .animate-fadeIn {
        animation: fadeIn 0.5s ease-out forwards;
        opacity: 0;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const skills = [
    'Flutter', 'Dart', 'ReactJS', 'JavaScript', 'Java', 'C/C++',
    'Firebase', 'Clean Architecture', 'BLoC', 'GetX', 'CI/CD',
    'REST API', 'Git', 'Postman', 'Android Studio', 'Xcode','Microsoft Dynamics 365 (CRM)','Power Apps'
  ];

  const experiences = [
    {
      company: "Unity Infotech Solutions LLC (Finance House)",
      position: "Senior Flutter Developer",
      period: "Nov 2024 - Present",
      location: "Dubai",
      description: "As a Senior Flutter Developer at Unity Infotech in the UAE, I spearheaded the development of a cutting-edge financial application. My responsibilities included designing and implementing features for card payments, money transfers, bill payments, and loan applications. I successfully delivered a comprehensive personal finance management solution, enhancing user experience and financial control"
    },
    {
      company: "Innovecture Pvt Ltd",
      position: "Senior Flutter Developer", 
      period: "May 2023 - Nov 2024",
      location: "Pune",
      description: "Worked on a healthcare application called Pine App, where I was responsible for optimizing the performance of the Flutter application, developing centralized and reusable UI components, implementing new features based on business requirements, and resolving production-level bugs to maintain application stability and enhance user experience."
    },
    {
      company: "Tech Mahindra",
      position: "Senior Flutter Developer",
      period: "May 2021 - Feb 2023", 
      location: "Mumbai",
      description: "Contributed as one of the core developers to Reliance's in-house HR application, PeopleFirst, used across 4 business units by over half a million employees. I was responsible for building complex, dynamic UIs and advanced search/filter functionalities to manage large datasets efficiently. I collaborated with cross-functional teams, deploying features via Azure DevOps pipelines with automated ticket tracking. I played a key role in planning, developing, and maintaining modules such as Carpool Services, Payroll Management, and Talent Marketplace, while also resolving production-level issues to ensure seamless performance"
    },
    {
      company: "Apptware Solutions LLP",
      position: "Senior Flutter Developer",
      period: "Dec 2019 - Apr 2021",
      location: "Pune", 
      description: "Spearheaded the formation of a Flutter team (7+ members), leading recruitment, training, and establishing a strong, collaborative development culture. Acted as the technical lead for mobile product design and architecture, overseeing the end-to-end development lifecycle — from requirement analysis and planning to deployment and maintenance."
    },
    {
      company: "Unitglo Solutions Pvt. Ltd",
      position: "Senior Android Developer",
      period: "Aug 2017 - Dec 2019",
      location: "Pune", 
      description: "Developed mobile interfaces using Java for two Android applications: one for ETH Digital Campus, enabling parents to access real-time updates on their children’s academic performance, attendance, and school events, with integrated communication tools to enhance parent-teacher interaction; and another for monitoring machine health, providing insights into performance parameters, alarms, trips, and preventive maintenance schedules."
    },
    {
      company: "ABMS Technologies Pvt. Ltd",
      position: "Android Developer",
      period: "May 2016 - May 2017",
      location: "Pune", 
      description: "Developed and deployed a hotel POS application aimed at streamlining operations and ensuring efficient post-deployment maintenance. Automated billing processes to reduce manual efforts and minimize errors, while integrating essential transaction functionalities. Additionally, created a comprehensive and user-friendly employee guide to support smooth system adoption and enhance training."
    }
  ];

  const projects = [
    { name: "HR Platform", platforms: "Android | iOS", description: "HR application for 1M+ Reliance employees" ,link:'https://play.google.com/store/apps/details?id=com.ril.jiopeoplefirst&hl=en_US&gl=US'},
    { name: "Foodies Corner", platforms: "Android", description: "Food delivery application" ,link:'https://apps.apple.com/in/app/foodies-corner/id1531050308'},
    { name: "POS App", platforms: "Android | iOS", description: "Empower your staff with a user-friendly POS system designed for speed and efficiency" ,link:'https://play.google.com/store/apps/details?id=com.unitglo.cartit_pos'},
    { name: "Investnation Fintech", platforms: "Android | iOS", description: "All-in-one mobile solution for managing your finances—track salary, check balances, view transactions, and pay bills with ease" ,link:'https://play.google.com/store/apps/details?id=com.fhc.InvestNation&hl=en_US'},
    { name: "FH Personal App Fintech", platforms: "Android | iOS", description: "Contributed to the development of Investnation, a user-friendly investment app offering curated plans, real-time fund access, and seamless onboarding—powered by Finance House" ,link:'https://play.google.com/store/apps/details?id=com.fh.payday&hl=en_US'},
    { name: "Health Care", platforms: "Android | iOS", description: "PineApp is transforming healthcare access by putting essential services at your fingertips—keeping you connected to your care anytime, anywhere." ,link:'https://play.google.com/store/apps/details?id=com.osellus.android.pineapp&hl=en_US'},
    { name: "Edu Tech", platforms: "Android | iOS", description: "E-learning and purchase platform ." ,link:'https://play.google.com/store/apps/details?id=com.matriyeacademy.student'},
    { name: "ETH parent App", platforms: "iOS | Android", description: "Digital Campus Parent App",link:'https://play.google.com/store/apps/dev?id=6840968501317174301&hl=en_IN&gl=US' },
    { name: "Foreign Education Admin App", platforms: "Android | iOS", description: "Full suite of services that helps you empower your students.",link:'https://apkpure.com/msm-unify-agent-connect/com.agent.msm_unify' },
    
  ];

  const testimonials = [
    {
      name: "Arun Jadhav,Pune",
      position: "Team Lead",
      content: "Dipak always maintained a good quality work and performed well under critical time situation. His ability to understand business requirement so quickly is really good. He has good habit of exploring the similar kind of product in which he is already working and always come up with new features. He has nice and polite communication skills. I wish him best of luck for his future success.",
    
    },
    {
      name: "Akash Kulkarni, Pune",
      position: "Manager",
      content: "Dipak routinely delivered excellent work and performed well in time-critical circumstances. He has a remarkable talent for understanding business objectives right away. He consistently thinks of new features and has an excellent habit of investigating alternatives to the ones he is currently working on. He has friendly and courteous communication. I hope he succeeds in all of his future endeavors.",
    
    },
    {
      name: "Sawan Pimpalkhare, Bengaluru, Karnataka",
      position: "Senior Flutter Developer",
      content: "Dipak sir writes the fastest, best and most cleanest code I have seen . He is passionate about his work and he is also the primary reason I feel like going to office everyday even in covid times . Hes the kind of person who will be studying about new packages and new plugins even on weekends . I have learned so much from him . He inspires me to be a better developer everyday.",
      
    },
    {
      name: "Mayur Kharat, Pune",
      position: "Fullstack Architect",
      content: "We’ve joined our hands on several projects, and Dipak is one of the best people I had as a partner. I highly recommend his expertise to any person looking for an Mobile Development. He is the most profound person I have met, and his ability to tackle any problem is remarkable and with a warm smile. Dipak would become an appreciated member of any team.",
      
    },
    {
      name: "Tushar Shete, Pune",
      position: "Java Developer",
      content: "Dipak is one of the best developers I have worked with. He is the guy who knows how to handle critical scenarios calmly and have always come with a refined solution. He is passionate about his work. I would love to be a part of such a developers.",
      
    },
    {
      name: "Kiran Shinde, Pune",
      position: "Senior Java Developer",
      content: "It was an awesome experience working with Dipak. We have worked on the same project and Dipak is having excellence in flutter technology and hands-on experience in efficiently solving critical scenarios. Amicable and passionate about the work.",
      
    },
    {
      name: "Manoj Waghmare, Pune",
      position: "Flutter Developer",
      content: "It was an great learning experience working with Dipak. We worked on the few project together and Dipak is having excellence in flutter technology and hands-on experience in efficiently solving critical scenarios.",
      
    }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
      setIsMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-black/20 backdrop-blur-lg z-50 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">DS</span>
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Dipak Sonawane
              </span>
            </div>
            
            <div className="hidden md:flex space-x-8">
              {['home', 'about', 'experience', 'projects', 'contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item)}
                  className={`capitalize transition-colors duration-300 hover:text-blue-400 ${
                    activeSection === item ? 'text-blue-400' : 'text-gray-300'
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>

            <button
              className="md:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-black/90 backdrop-blur-lg">
            <div className="px-4 py-2 space-y-2">
              {['home', 'about', 'experience', 'projects', 'contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item)}
                  className="block w-full text-left px-4 py-2 capitalize hover:bg-white/10 rounded-lg transition-colors"
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="min-h-screen flex items-center justify-center relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-purple-600/10"></div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-blue-500/20 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-32 h-32 bg-purple-500/20 rounded-full blur-xl animate-pulse delay-700"></div>
        <div className="absolute bottom-20 left-20 w-24 h-24 bg-pink-500/20 rounded-full blur-xl animate-pulse delay-1000"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10 w-full">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center min-h-screen py-20">
            
            {/* Left Side - Content */}
            <div className={`space-y-8 transform transition-all duration-1000 delay-300 ${
              isLoaded ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'
            }`}>
              {/* Badge */}
              <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-full border border-blue-500/30 backdrop-blur-sm">
                <div className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></div>
                <span className="text-sm text-blue-300">Available for Projects</span>
              </div>
              
              {/* Main Heading */}
              <div className="space-y-4">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight">
                  <span className="block text-white">Hello, I'm</span>
                  <span className="block bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                    Dipak Sonawane
                  </span>
                </h1>
                
                {/* Typing Effect for Role */}
                <div className="h-16 flex items-center">
                  <p className="text-xl sm:text-2xl lg:text-3xl text-gray-300 font-light">
                    <span className="text-blue-400">></span> Senior Flutter Developer
                    <span className="animate-pulse text-purple-400">|</span>
                  </p>
                </div>
              </div>
              
              {/* Skills Tags */}
              <div className="flex flex-wrap gap-2">
                {['Flutter', 'Dart', 'iOS', 'Android', 'BLoC', 'Firebase', 'Clean Architecture','Microsoft 365 CRM'].map((skill, index) => (
                  <span
                    key={skill}
                    className={`px-3 py-1 text-xs bg-white/10 backdrop-blur-sm rounded-full border border-white/20 text-gray-300 hover:bg-white/20 transition-all duration-300 transform hover:scale-105 ${
                      isLoaded ? 'animate-fadeIn' : 'opacity-0'
                    }`}
                    style={{ animationDelay: `${800 + index * 100}ms` }}
                  >
                    {skill}
                  </span>
                ))}
              </div>
              
              {/* Description */}
              <p className="text-lg text-gray-400 leading-relaxed max-w-xl">
                I am a highly skilled Software Engineer with <span className="text-blue-400 font-semibold">10 years</span> of experience, specializing in Flutter, Firebase, GraphQL, and cloud technologies. I have been dedicated to Flutter development, crafting high-performance applications across Android, iOS, and Web. My expertise lies in building scalable, pixel-perfect UIs, optimizing performance, and integrating advanced native features.
              
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => scrollToSection('projects')}
                  className="group px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full hover:from-blue-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-blue-500/25"
                >
                  <span className="flex items-center justify-center space-x-2">
                    <span>View My Work</span>
                    <ExternalLink size={16} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </button>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="px-8 py-4 border border-white/30 rounded-full hover:bg-white/10 hover:border-white/50 transition-all duration-300 backdrop-blur-sm"
                >
                  Get In Touch
                </button>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 pt-8">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-400">10</div>
                  <div className="text-sm text-gray-400">Years Experience</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-400">1M+</div>
                  <div className="text-sm text-gray-400">App Users</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-pink-400">30+</div>
                  <div className="text-sm text-gray-400">Projects</div>
                </div>
              </div>
            </div>
            
            {/* Right Side - Image/Visual */}
            <div className={`relative transform transition-all duration-1000 delay-500 ${
              isLoaded ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
            }`}>
              {/* Main Image Container */}
              <div className="relative">
                {/* Glowing Background */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 rounded-3xl blur-3xl opacity-30 animate-pulse"></div>
                
                {/* Profile Image */}
                <div className="relative bg-gradient-to-br from-blue-500/20 to-purple-600/20 rounded-3xl p-8 backdrop-blur-sm border border-white/10">
                  <div className="w-80 h-80 mx-auto bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center text-8xl font-bold text-white shadow-2xl relative overflow-hidden">
                    {/* You can replace this with actual image */}
                              <img src="../dipak_image.jpeg" alt="Dipak Sonawane Logo" />
                    {/* Animated overlay */}
                    <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/10 to-transparent animate-shimmer"></div>
                  </div>
                </div>
                
                {/* Floating Cards */}
                <div className="absolute -top-6 -right-6 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 animate-float">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                      <Code size={16} className="text-white" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-white">Flutter Expert</div>
                      <div className="text-xs text-gray-400">Cross Platform</div>
                    </div>
                  </div>
                </div>
                
                <div className="absolute -bottom-6 -left-6 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 animate-float delay-1000">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                      <Briefcase size={16} className="text-white" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-white">Dubai Based</div>
                      <div className="text-xs text-gray-400">Available Remote</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/50 rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
            About Me
          </h2>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl font-semibold mb-6">Senior Flutter Developer</h3>
              <p className="text-gray-300 mb-6 leading-relaxed">
                With over 10 years of experience in mobile development, I specialize in creating high-performance, 
                scalable Flutter applications. I've successfully delivered projects serving hundreds of thousands 
                of users across various industries including finance, healthcare, and e-commerce.
              </p>
             
              <div className="flex items-center space-x-4 text-gray-400">
                <MapPin size={20} />
                <span>Dubai, UAE | Pune, India</span>
              </div>
            </div>
            <div>
              <h3 className="text-2xl font-semibold mb-6">Technical Skills</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {skills.map((skill, index) => (
                  <div
                    key={skill}
                    className={`bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2 text-center text-sm hover:bg-white/20 transition-all duration-300 transform hover:scale-105 ${
                      isLoaded ? 'animate-pulse' : ''
                    }`}
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    {skill}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="py-20 px-4 bg-black/20">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
            Work Experience
          </h2>
          <div className="space-y-8">
            {experiences.map((exp, index) => (
              <div
                key={index}
                className="bg-white/5 backdrop-blur-sm rounded-xl p-6 hover:bg-white/10 transition-all duration-300 transform hover:scale-[1.02]"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-blue-400">{exp.position}</h3>
                    <h4 className="text-lg text-gray-300">{exp.company}</h4>
                  </div>
                  <div className="text-right text-gray-400">
                    <p>{exp.period}</p>
                    <p>{exp.location}</p>
                  </div>
                </div>
                <p className="text-gray-300 leading-relaxed">{exp.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Section */}
  <section id="projects" className="py-20 px-4">
  <div className="max-w-6xl mx-auto"> {/* Fixed typo: mx-au to mx-auto */}
    <h2 className="text-4xl font-bold text-center mb-16 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
      Featured Projects
    </h2>

    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 justify-center">
      {projects.map((project, index) => (
        <div
          key={index}
          className="bg-white/5 backdrop-blur-sm rounded-xl p-6 hover:bg-white/10 transition-all duration-300 transform hover:scale-105 group"
        >
          <div className="flex items-center justify-between mb-4">
            <Code className="text-blue-400" size={24} />
            {/* Open link in new tab if project.link exists */}
            {project.link && (
              <a
                href={project.link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 group-hover:text-white transition-colors"
              >
                <ExternalLink size={20} />
              </a>
            )}
          </div>
          <h3 className="text-xl font-semibold mb-2">{project.name}</h3>
          <p className="text-blue-400 text-sm mb-3">{project.platforms}</p>
          <p className="text-gray-300 text-sm leading-relaxed">{project.description}</p>
        </div>
      ))}
    </div>
  </div>
</section>

{/* Testimonials Section */}
<section className="py-20 px-4 bg-black/20">
  <div className="max-w-6xl mx-auto">
    <h2 className="text-4xl font-bold text-center mb-16 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
      Testimonials
    </h2>
    <div className="grid md:grid-cols-3 gap-8">
      {testimonials.map((testimonial, index) => (
        <div
          key={index}
          className="bg-white/5 backdrop-blur-sm rounded-xl p-6 hover:bg-white/10 transition-all duration-300 transform hover:scale-105"
        >
          {/* Quote icon at top */}
          <Quote className="text-purple-400 mb-4" size={24} />

          {/* Highlight or main content */}
        
          <p className="text-gray-300 mb-4 leading-relaxed">"{testimonial.content}"</p>

          {/* User details */}
          <div>
            <p className="font-semibold">{testimonial.name}</p>
            <p className="text-sm text-gray-400">{testimonial.position}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
</section>


      {/* Contact Section */}
      <section id="contact" className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-16 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
            Let's Work Together
          </h2>
          <p className="text-xl text-gray-300 mb-12">
            Ready to build something amazing? Let's discuss your next Flutter project.
          </p>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 hover:bg-white/10 transition-all duration-300">
              <Mail className="mx-auto mb-4 text-blue-400" size={32} />
              <h3 className="font-semibold mb-2">Email</h3>
              <p className="text-gray-300">sonawanedipak111@gmail.com</p>
            </div>
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 hover:bg-white/10 transition-all duration-300">
              <Phone className="mx-auto mb-4 text-blue-400" size={32} />
              <h3 className="font-semibold mb-2">Phone</h3>
              <p className="text-gray-300">+971 504557278</p>
            </div>
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 hover:bg-white/10 transition-all duration-300">
              <MapPin className="mx-auto mb-4 text-blue-400" size={32} />
              <h3 className="font-semibold mb-2">Location</h3>
              <p className="text-gray-300">Dubai, UAE</p>
            </div>
          </div>
          <div className="flex justify-center space-x-6">
            <a
              href="https://github.com/d2square"
              className="p-3 bg-white/10 rounded-full hover:bg-white/20 transition-all duration-300 transform hover:scale-110"
            >
              <Github size={24} />
            </a>
            <a
              href="https://linkedin.com/in/dipak-sonawane-02701ab2"
              className="p-3 bg-white/10 rounded-full hover:bg-white/20 transition-all duration-300 transform hover:scale-110"
            >
              <Linkedin size={24} />
            </a>
            <a
              href="mailto:sonawanedipak111@gmail.com"
              className="p-3 bg-white/10 rounded-full hover:bg-white/20 transition-all duration-300 transform hover:scale-110"
            >
              <Mail size={24} />
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-white/10">
        <div className="max-w-6xl mx-auto text-center text-gray-400">
          <p>&copy; 2025 Dipak Sonawane. All rights reserved.</p>
        
        </div>
      </footer>
    </div>
  );
};

export default FlutterPortfolio;
